/**
 * Lightweight hand-rolled hash router. No router library — just
 * hashchange listening + pattern matching against registered routes.
 */

import { noteRouteChange } from "./navigation";

export type RouteParams = Record<string, string>;
export type RouteHandler = (params: RouteParams) => void;

interface CompiledRoute {
  pattern: RegExp;
  keys: string[];
  handler: RouteHandler;
}

export class Router {
  private routes: CompiledRoute[] = [];
  private fallback: RouteHandler | null = null;
  private started = false;

  register(path: string, handler: RouteHandler): this {
    const keys: string[] = [];
    const patternSource = path
      .replace(/\/+$/, "")
      .split("/")
      .map((segment) => {
        if (segment.startsWith(":")) {
          keys.push(segment.slice(1));
          return "([^/]+)";
        }
        return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      })
      .join("/");
    this.routes.push({ pattern: new RegExp(`^${patternSource || "/"}$`), keys, handler });
    return this;
  }

  notFound(handler: RouteHandler): this {
    this.fallback = handler;
    return this;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener("hashchange", () => this.resolve());
    this.resolve();
  }

  navigate(path: string): void {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    if (this.currentPath() === normalized) {
      this.resolve();
      return;
    }
    window.location.hash = `#${normalized}`;
  }

  currentPath(): string {
    const raw = window.location.hash.replace(/^#/, "");
    return raw || "/";
  }

  private resolve(): void {
    const path = this.normalizePath(this.currentPath());
    noteRouteChange(path);
    for (const route of this.routes) {
      const match = route.pattern.exec(path);
      if (match) {
        const params: RouteParams = {};
        route.keys.forEach((key, i) => {
          params[key] = decodeURIComponent(match[i + 1]);
        });
        route.handler(params);
        return;
      }
    }
    this.fallback?.({});
  }

  private normalizePath(path: string): string {
    if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
    return path;
  }
}
